import type { ReactNode } from 'react';
import { useMemo, useState, useContext, createContext } from 'react';

/**
 * Rapor ekranları arasında paylaşılan kademeli seçim (Firma → Şube → Çalışan).
 * Hafif bir context; global store yerine yalnızca seçili kapsamı tutar.
 */
export interface ReportScopeState {
  companyId: string | null;
  branchId: string | null;
  employeeId: string | null;
  setCompanyId: (id: string | null) => void;
  setBranchId: (id: string | null) => void;
  setEmployeeId: (id: string | null) => void;
  reset: () => void;
}

const ReportScopeContext = createContext<ReportScopeState | undefined>(undefined);

export function ReportScopeProvider({ children }: { children: ReactNode }) {
  const [companyId, setCompanyIdState] = useState<string | null>(null);
  const [branchId, setBranchIdState] = useState<string | null>(null);
  const [employeeId, setEmployeeId] = useState<string | null>(null);

  const value = useMemo<ReportScopeState>(() => {
    // Üst seviye değişince alt seçimleri sıfırla (cascade reset).
    const setCompanyId = (id: string | null) => {
      setCompanyIdState(id);
      setBranchIdState(null);
      setEmployeeId(null);
    };
    const setBranchId = (id: string | null) => {
      setBranchIdState(id);
      setEmployeeId(null);
    };
    const reset = () => {
      setCompanyIdState(null);
      setBranchIdState(null);
      setEmployeeId(null);
    };
    return {
      companyId,
      branchId,
      employeeId,
      setCompanyId,
      setBranchId,
      setEmployeeId,
      reset,
    };
  }, [companyId, branchId, employeeId]);

  return <ReportScopeContext.Provider value={value}>{children}</ReportScopeContext.Provider>;
}

export function useReportScope(): ReportScopeState {
  const ctx = useContext(ReportScopeContext);
  if (!ctx) {
    throw new Error('useReportScope, ReportScopeProvider içinde kullanılmalıdır');
  }
  return ctx;
}
